import { createContext, useContext, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { findClientParamValue } from "@/util/common_function";

/**
 * Tracks which fields on a form were auto-populated from a jacket/prior-record
 * lookup and haven't yet been confirmed by the user. Mirrors the legacy ASP
 * RequireVerificationOfJacketFields behavior: pink highlight until the user
 * edits the field or presses Alt+Q, and Save is blocked while any remain.
 */
const FieldVerificationContext = createContext({
  requireVerification: false,
  isUnverified: () => false,
  markUnverified: () => {},
  markVerified: () => {},
  hasUnverifiedFields: () => false,
  getUnverifiedFieldNames: () => [],
  clearAll: () => {},
});

/**
 * @param {string} moduleContext - client-param context key, e.g. "Bookings",
 *   "BookingsJacketsJackets", "VictimNotification", "InmateMovementRules".
 *   Matches the ASP AppContext naming so admins can reuse familiar names
 *   when configuring RequireVerificationOfJacketFields per module.
 */
export const useFieldVerificationState = (moduleContext) => {
  const clientParamsList = useSelector((s) => s?.clientParams?.list);
  const [unverifiedFields, setUnverifiedFields] = useState(() => new Set());

  const rawValue = findClientParamValue(
    clientParamsList,
    "RequireVerificationOfJacketFields",
    moduleContext,
  );
  // Opt-in per module: no client-param row for this context (e.g. Warrants,
  // which isn't configured today) means the pink-field/save-block behavior
  // stays off, not on. Only an explicit row (True/False) decides it.
  const requireVerification =
    rawValue !== null && String(rawValue).toLowerCase() !== "false";

  const isUnverified = useCallback(
    (name) => unverifiedFields.has(name),
    [unverifiedFields],
  );

  const markUnverified = useCallback((nameOrNames) => {
    const names = Array.isArray(nameOrNames) ? nameOrNames : [nameOrNames];
    setUnverifiedFields((prev) => {
      const next = new Set(prev);
      names.forEach((n) => n && next.add(n));
      return next;
    });
  }, []);

  const markVerified = useCallback((nameOrNames) => {
    const names = Array.isArray(nameOrNames) ? nameOrNames : [nameOrNames];
    setUnverifiedFields((prev) => {
      if (!names.some((n) => prev.has(n))) return prev;
      const next = new Set(prev);
      names.forEach((n) => next.delete(n));
      return next;
    });
  }, []);

  const hasUnverifiedFields = useCallback(
    () => requireVerification && unverifiedFields.size > 0,
    [requireVerification, unverifiedFields],
  );

  const getUnverifiedFieldNames = useCallback(
    () => Array.from(unverifiedFields),
    [unverifiedFields],
  );

  // Call when starting a fresh session on the same mounted form (e.g. a modal
  // is reopened for a different record) so stale unverified state from a
  // previous open doesn't leak in.
  const clearAll = useCallback(() => setUnverifiedFields(new Set()), []);

  return {
    requireVerification,
    isUnverified,
    markUnverified,
    markVerified,
    hasUnverifiedFields,
    getUnverifiedFieldNames,
    clearAll,
  };
};

export const useFieldVerification = () => useContext(FieldVerificationContext);

/**
 * Alt+Q (or Cmd+Q) while focused in a field clears its unverified state.
 * Wire the returned handler directly onto the field's own `onKeyDown` prop —
 * not a wrapping element — since Form.Item clones only its direct child to
 * wire up value/onChange, and an intervening wrapper would break that binding.
 */
export const makeVerifyKeyDownCapture = (
  fieldName,
  unverified,
  markVerified,
) => {
  if (!unverified) return undefined;
  return (e) => {
    if ((e.altKey || e.metaKey) && e.key?.toLowerCase() === "q") {
      e.preventDefault();
      markVerified(fieldName);
    }
  };
};

export default FieldVerificationContext;
